/**
 * AuditCompliance - ISO 45001/9001 compliance tracking & clause mapping
 * 
 * Features:
 *   - ISO 45001:2018 and ISO 9001:2015 clause structure
 *   - Map audit template items to ISO clauses
 *   - Coverage analysis showing which clauses are audited
 *   - Compliance gap identification
 *   - Regulatory framework tracking
 */

import React, { useState, useEffect } from 'react';
import {
  Scale, CheckCircle2, XCircle, AlertTriangle, Search, ChevronRight,
  ChevronDown, BookOpen,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Badge } from '@shared/components/ui/badge';
import { Progress } from '@shared/components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@shared/components/ui/select';
// import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@shared/components/ui/tooltip';
import { supabase } from '@shared/supabaseClient';

// ─── ISO Clause Structures ─────────────────────────────────────────

const ISO_45001_CLAUSES = [
  {
    number: '4', title: 'Context of the Organization',
    sub: [
      { number: '4.1', title: 'Understanding the organization and its context' },
      { number: '4.2', title: 'Understanding the needs and expectations of workers and other interested parties' },
      { number: '4.3', title: 'Determining the scope of the OH&S management system' },
      { number: '4.4', title: 'OH&S management system' },
    ],
  },
  {
    number: '5', title: 'Leadership and Worker Participation',
    sub: [
      { number: '5.1', title: 'Leadership and commitment' },
      { number: '5.2', title: 'OH&S policy' },
      { number: '5.3', title: 'Organizational roles, responsibilities and authorities' },
      { number: '5.4', title: 'Consultation and participation of workers' },
    ],
  },
  {
    number: '6', title: 'Planning',
    sub: [
      { number: '6.1', title: 'Actions to address risks and opportunities' },
      { number: '6.1.1', title: 'General' },
      { number: '6.1.2', title: 'Hazard identification and assessment of risks and opportunities' },
      { number: '6.1.3', title: 'Determination of legal requirements and other requirements' },
      { number: '6.1.4', title: 'Planning action' },
      { number: '6.2', title: 'OH&S objectives and planning to achieve them' },
    ],
  },
  {
    number: '7', title: 'Support',
    sub: [
      { number: '7.1', title: 'Resources' },
      { number: '7.2', title: 'Competence' },
      { number: '7.3', title: 'Awareness' },
      { number: '7.4', title: 'Communication' },
      { number: '7.5', title: 'Documented information' },
    ],
  },
  {
    number: '8', title: 'Operation',
    sub: [
      { number: '8.1', title: 'Operational planning and control' },
      { number: '8.1.1', title: 'General' },
      { number: '8.1.2', title: 'Eliminating hazards and reducing OH&S risks' },
      { number: '8.1.3', title: 'Management of change' },
      { number: '8.1.4', title: 'Procurement' },
      { number: '8.2', title: 'Emergency preparedness and response' },
    ],
  },
  {
    number: '9', title: 'Performance Evaluation',
    sub: [
      { number: '9.1', title: 'Monitoring, measurement, analysis and performance evaluation' },
      { number: '9.1.1', title: 'General' },
      { number: '9.1.2', title: 'Evaluation of compliance' },
      { number: '9.2', title: 'Internal audit' },
      { number: '9.3', title: 'Management review' },
    ],
  },
  {
    number: '10', title: 'Improvement',
    sub: [
      { number: '10.1', title: 'General' },
      { number: '10.2', title: 'Incident, nonconformity and corrective action' },
      { number: '10.3', title: 'Continual improvement' },
    ],
  },
];

const ISO_9001_CLAUSES = [
  {
    number: '4', title: 'Context of the Organization',
    sub: [
      { number: '4.1', title: 'Understanding the organization and its context' },
      { number: '4.2', title: 'Understanding the needs and expectations of interested parties' },
      { number: '4.3', title: 'Determining the scope of the quality management system' },
      { number: '4.4', title: 'Quality management system and its processes' },
    ],
  },
  {
    number: '5', title: 'Leadership',
    sub: [
      { number: '5.1', title: 'Leadership and commitment' },
      { number: '5.2', title: 'Policy' },
      { number: '5.3', title: 'Organizational roles, responsibilities and authorities' },
    ],
  },
  {
    number: '6', title: 'Planning',
    sub: [
      { number: '6.1', title: 'Actions to address risks and opportunities' },
      { number: '6.2', title: 'Quality objectives and planning to achieve them' },
      { number: '6.3', title: 'Planning of changes' },
    ],
  },
  {
    number: '7', title: 'Support',
    sub: [
      { number: '7.1', title: 'Resources' },
      { number: '7.2', title: 'Competence' },
      { number: '7.3', title: 'Awareness' },
      { number: '7.4', title: 'Communication' },
      { number: '7.5', title: 'Documented information' },
    ],
  },
  {
    number: '8', title: 'Operation',
    sub: [
      { number: '8.1', title: 'Operational planning and control' },
      { number: '8.2', title: 'Requirements for products and services' },
      { number: '8.3', title: 'Design and development of products and services' },
      { number: '8.4', title: 'Control of externally provided processes, products and services' },
      { number: '8.5', title: 'Production and service provision' },
      { number: '8.6', title: 'Release of products and services' },
      { number: '8.7', title: 'Control of nonconforming outputs' },
    ],
  },
  {
    number: '9', title: 'Performance Evaluation',
    sub: [
      { number: '9.1', title: 'Monitoring, measurement, analysis and evaluation' },
      { number: '9.2', title: 'Internal audit' },
      { number: '9.3', title: 'Management review' },
    ],
  },
  {
    number: '10', title: 'Improvement',
    sub: [
      { number: '10.1', title: 'General' },
      { number: '10.2', title: 'Nonconformity and corrective action' },
      { number: '10.3', title: 'Continual improvement' },
    ],
  },
];

// ─── Clause Row ─────────────────────────────────────────────────────
function ClauseRow({ clause, mappedItems, depth = 0, forceExpand = false }) {
  const [expanded, setExpanded] = useState(forceExpand);
  const hasChildren = clause.sub && clause.sub.length > 0;
  const coverage = mappedItems.filter(i =>
    i.requirement_reference?.includes(clause.number) ||
    i.regulatory_source?.includes(clause.number)
  );

  const statusIcon = coverage.length > 0
    ? <CheckCircle2 className="h-4 w-4 text-green-500" />
    : <XCircle className="h-4 w-4 text-red-400" />;

  return (
    <>
      <div
        className={`flex items-center gap-2 px-3 py-2 hover:bg-muted/30 transition-colors cursor-pointer border-b border-border/50`}
        style={{ paddingLeft: `${12 + depth * 20}px` }}
        onClick={() => hasChildren && setExpanded(!expanded)}
      >
        {hasChildren ? (
          expanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        ) : (
          <div className="w-3.5 shrink-0" />
        )}
        {statusIcon}
        <span className="font-mono text-xs text-muted-foreground w-10 shrink-0">{clause.number}</span>
        <span className="text-sm flex-1">{clause.title}</span>
        {coverage.length > 0 && (
          <Badge variant="outline" className="text-[10px] text-green-600 border-green-300">
            {coverage.length} item{coverage.length !== 1 ? 's' : ''}
          </Badge>
        )}
      </div>
      {expanded && hasChildren && clause.sub.map(sub => (
        <ClauseRow
          key={sub.number}
          clause={sub}
          mappedItems={mappedItems}
          depth={depth + 1}
          forceExpand={forceExpand}
        />
      ))}
    </>
  );
}

// ─── Main Compliance Component ──────────────────────────────────────
export default function AuditCompliance({ user: _user }) {
  const [standard, setStandard] = useState('iso45001');
  const [search, setSearch] = useState('');
  const [mappedItems, setMappedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandAll, setExpandAll] = useState(false);

  useEffect(() => { loadMappedItems(); }, []);

  const loadMappedItems = async () => {
    try {
      setIsLoading(true);
      // Get all template items that have ISO references
      const { data, error } = await supabase
        .from('audit_template_items')
        .select('id, item_text, item_code, requirement_reference, regulatory_source')
        .or('requirement_reference.neq.null,regulatory_source.neq.null')
        .limit(500);

      if (error) throw error;
      setMappedItems(data || []);
    } catch (error) {
      console.error('Error loading mapped items:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const clauses = standard === 'iso45001' ? ISO_45001_CLAUSES : ISO_9001_CLAUSES;
  const standardLabel = standard === 'iso45001' ? 'ISO 45001:2018' : 'ISO 9001:2015';

  // Calculate coverage stats
  const allSubClauses = clauses.flatMap(c => [c, ...(c.sub || [])]);
  const coveredClauses = allSubClauses.filter(clause =>
    mappedItems.some(i =>
      i.requirement_reference?.includes(clause.number) ||
      i.regulatory_source?.includes(clause.number)
    )
  );
  const coveragePercent = allSubClauses.length > 0
    ? Math.round((coveredClauses.length / allSubClauses.length) * 100)
    : 0;

  const filteredClauses = search
    ? clauses.filter(c =>
        c.title.toLowerCase().includes(search.toLowerCase()) ||
        c.number.includes(search) ||
        c.sub?.some(s => s.title.toLowerCase().includes(search.toLowerCase()) || s.number.includes(search))
      )
    : clauses;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Compliance Tracking</h2>
          <p className="text-sm text-muted-foreground">
            Map audit items to ISO clauses and track compliance coverage
          </p>
        </div>
        <Select value={standard} onValueChange={setStandard}>
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="iso45001">ISO 45001:2018 (OH&S)</SelectItem>
            <SelectItem value="iso9001">ISO 9001:2015 (Quality)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Coverage Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <Scale className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{coveragePercent}%</p>
              <p className="text-xs text-muted-foreground">Clause Coverage</p>
            </div>
          </div>
          <Progress
            value={coveragePercent}
            className={`mt-3 h-2 ${coveragePercent >= 80 ? '[&>div]:bg-green-500' : coveragePercent >= 50 ? '[&>div]:bg-amber-500' : '[&>div]:bg-red-500'}`}
          />
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{coveredClauses.length}</p>
              <p className="text-xs text-muted-foreground">Clauses Covered</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/10">
              <AlertTriangle className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{allSubClauses.length - coveredClauses.length}</p>
              <p className="text-xs text-muted-foreground">Gaps Identified</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search & Controls */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={`Search ${standardLabel} clauses...`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setExpandAll(!expandAll)}
        >
          {expandAll ? 'Collapse All' : 'Expand All'}
        </Button>
      </div>

      {/* Clause Structure */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-amber-500" />
            {standardLabel} — Clause Structure
          </CardTitle>
          <CardDescription>
            <CheckCircle2 className="h-3 w-3 text-green-500 inline mr-1" />
            Covered by audit items
            <XCircle className="h-3 w-3 text-red-400 inline ml-3 mr-1" />
            Not yet covered (gap)
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="divide-y divide-border/30">
              {filteredClauses.map(clause => (
                <ClauseRow
                  key={clause.number}
                  clause={clause}
                  mappedItems={mappedItems}
                  forceExpand={expandAll}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mapped Items Summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Mapped Audit Items</CardTitle>
          <CardDescription>
            {mappedItems.length} items have ISO/regulatory references
          </CardDescription>
        </CardHeader>
        <CardContent>
          {mappedItems.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No items with ISO references found. Add requirement references when building audit templates.
            </p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {mappedItems.slice(0, 20).map(item => (
                <div key={item.id} className="flex items-center gap-2 text-sm p-2 rounded hover:bg-muted/30">
                  <span className="font-mono text-xs text-muted-foreground w-10 shrink-0">{item.item_code}</span>
                  <span className="flex-1 truncate">{item.item_text}</span>
                  <Badge variant="outline" className="text-[10px] text-blue-600 border-blue-300 shrink-0">
                    {item.requirement_reference || item.regulatory_source}
                  </Badge>
                </div>
              ))}
              {mappedItems.length > 20 && (
                <p className="text-xs text-muted-foreground text-center pt-2">
                  +{mappedItems.length - 20} more items
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
